import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeFacetModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsCanadaWebsite } from "@midland-industries/Components/WebsiteSelector";
import ProductPrice, { ProductPriceStyles } from "@midland-industries/Overrides/Components/ProductPrice";
import ProductListCondensedActions from "@midland-industries/Widgets/ProductList/ProductListCondensedActions";
import ProductListCondensedProductInformation from "@midland-industries/Widgets/ProductList/ProductListCondensedProductInformation";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/**
 * ChangeLog:
 * 20230411: DLD: Restyling. Removing attributes as the names are too long are are pushing the other items out of alignment.
 * - Removing dynamic attributes from PLP condensed view;
 * - Replacing with Description (product.productTitle).
 */
interface OwnProps {
    products: ProductModel[];
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    productInfosByProductId: state.pages.productList.productInfosByProductId,
    visibleColumnNames: state.pages.productList.visibleColumnNames,
    tableColumns: state.pages.productList.tableColumns,
    isAuthenticated: state.context.session.isAuthenticated,
    isCanada: getIsCanadaWebsite(state)
});

type Props = ReturnType<typeof mapStateToProps> & ProductCardSelections & OwnProps;

export interface ProductListCondensedStyles {
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    descriptionHeader?: DataTableHeaderProps;
    attributesHeader?: DataTableHeaderProps;
    availabilityHeader?: DataTableHeaderProps;
    priceHeader?: DataTableHeaderProps;
    unitOfMeasureHeader?: DataTableHeaderProps;
    actionsHeader?: DataTableHeaderProps;
    descriptionCell?: DataTableCellProps;
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    productImageStyles?: ProductListProductImageStyles;
    attributesCell?: DataTableCellProps;
    availabilityCell?: DataTableCellProps;
    availabilityStyles?: ProductAvailabilityStyles;
    priceCell?: DataTableCellProps;
    productPrice?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    unitOfMeasureCell?: DataTableCellProps;
    unitOfMeasureText?: TypographyPresentationProps;
    actionsCell?: DataTableCellProps;
    sponsoredText?: TypographyPresentationProps;
}

export const actionsStyles: ProductListCondensedStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    productImageStyles: {
        compareCheckbox: {
            css: css`
                align-self: center;
                label {
                    display: none;
                }
            `,
        },
    },
    actionsHeader: {
        css: css`
            position: sticky;
            right: 0;
            background-clip: padding-box;
        `,
    },
    attributesHeader: {},
    descriptionHeader: {},
    descriptionCell: {
        css: css`
            white-space: nowrap !important;
        `,
    },
    attributesCell: {
        css: css`
            white-space: normal;
        `,
    },
    availabilityCell: {
        css: css`
            white-space: normal;
        `,
    },
    priceCell: {
        css: css`
            white-space: nowrap;
        `,
    },
    availabilityStyles: {
        messageText: {
            css: css`
                white-space: nowrap;
            `,
        },
    },
    actionsCell: {
        css: css`
            position: sticky;
            right: 0;
            background-color: inherit;
            background-clip: padding-box;
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = actionsStyles;

const ProductListCondensed = ({
    products,
    productSettings,
    productInfosByProductId,
    visibleColumnNames,
    tableColumns,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    isAuthenticated,
    isCanada
}: Props) => {
    const productListRows = products.map((product, idx) => {
        const productInfo = productInfosByProductId[product.id];
        if (!productInfo) {
            return;
        }

        const productContext: ProductContextModel = {
            product,
            productInfo,
        };
        return (
            <ProductListProductContext product={product} key={product.id} idx={idx}>
                <DataTableRow key={product.id} data-test-selector={`productListProductCard${product.id}`}>
                    <DataTableCell {...styles.descriptionCell}>
                        {product.isSponsored && (
                            <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                        )}
                        <ProductListCondensedProductInformation
                            showAttributes={false}
                            showAvailability={false}
                            showBrand={false}
                            showPartNumbers={showPartNumbers}
                            showTitle={false}
                        />
                    </DataTableCell>

                    <DataTableCell {...styles.descriptionCell}>
                        <Typography>{product.productTitle}</Typography>
                    </DataTableCell>

                    {isAuthenticated && (
                        <>
                            <DataTableCell {...styles.priceCell}>
                            {productSettings.showInventoryAvailability && showAvailability && (
                            <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                            )}
                            </DataTableCell>
                            <DataTableCell {...styles.priceCell}>
                                <ProductPrice
                                    product={productContext}
                                    showLabel={false}
                                    showUnitOfMeasure={true}
                                    showPack={false}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    extendedStyles={styles.productPrice}
                                />
                                <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                            </DataTableCell>
                            {!isCanada && (
                                <DataTableCell {...styles.priceCell}>
                                <ProductPrice
                                    product={productContext}
                                    showLabel={false}
                                    showUnitOfMeasure={true}
                                    showPack={false}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    extendedStyles={styles.productPrice}
                                    overwriteWithListPrice={true}
                                />
                                <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                            </DataTableCell>
                            )}
                            
                            <DataTableCell {...styles.actionsCell}>
                                <ProductListCondensedActions showAddToList={showAddToList} showPrice={false} />
                            </DataTableCell>
                        </>
                    )}
                </DataTableRow>
            </ProductListProductContext>
        );
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable {...styles.dataTable}>
                <DataTableHead>
                    <DataTableHeader {...styles.descriptionHeader}>{translate("Part #")}</DataTableHeader>
                    <DataTableHeader {...styles.descriptionHeader}>{translate("Description")}</DataTableHeader>
                    {isAuthenticated && !isCanada &&(
                        <>
                            <DataTableHeader {...styles.priceHeader}>{translate("Availability")}</DataTableHeader>
                            <DataTableHeader {...styles.priceHeader}>{translate("Net Price")}</DataTableHeader>
                            <DataTableHeader {...styles.priceHeader}>{translate("List Price")}</DataTableHeader>
                            <DataTableHeader {...styles.priceHeader}>{translate("Order")}</DataTableHeader>
                        </>
                    )}
                    {isAuthenticated && isCanada && (
                        <>
                            <DataTableHeader {...styles.priceHeader}>{translate("Availability")}</DataTableHeader>
                            <DataTableHeader {...styles.priceHeader}>{translate("Net Price")}</DataTableHeader>
                            <DataTableHeader {...styles.priceHeader}>{translate("Order")}</DataTableHeader>
                        </>
                    )}
                </DataTableHead>
                <DataTableBody>{productListRows}</DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductListCondensed);
